import React, { useState, useEffect } from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import TextArea from "@/components/atoms/TextArea";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@/components/atoms/Button";
import { Star } from "lucide-react";
import axios from "axios";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { toast } from "react-toastify";

const ratingFormSchema = z.object({
  rating_message: z.string().nullable(),
});

type RatingFormData = z.infer<typeof ratingFormSchema>;

type Props = {
  ticket: {id: number, display_id: string, subject: string, is_rateable: boolean, rating_message: string};
  star: number;
};

const RateTicketContent = ({ ticket, star }: Props) => {
  const rateForm = useForm<RatingFormData>({
    resolver: zodResolver(ratingFormSchema),
    defaultValues: {
      rating_message: "",
    },
  });

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<{success: boolean, message: string} | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute("content") || "" : "";
  };

  const handleSubmit = async (data: RatingFormData) => {
    if (!executeRecaptcha) {
      setCaptchaError("reCAPTCHA not ready. Please try again.");
      return;
    }

    setIsSubmitting(true);

    try {
      try {
        const recaptchaToken = await executeRecaptcha("submit_action");
        if (!recaptchaToken) {
          setCaptchaError("reCAPTCHA failed to execute. Please try again.");
          return;
        }

        const response = await axios.post(
          `/tickets/${ticket.id}/rate`,
          {
            ticket: {
              ...data,
              rating_message: data.rating_message,
              rating: star,
            },
            recaptcha_token: recaptchaToken,
          },
          {
            headers: {
              "X-CSRF-Token": getCsrfToken(),
            },
          }
        );
  
        if (response.data.success) {
          setToastMessage({ success: true, message: response.data.message });
  
          setTimeout(() => {
            window.location.href = "/";
          }, 2000);
        } else {
          if (response.data.recaptcha_message) {
            setCaptchaError(response.data.recaptcha_message);
          } else {
            setToastMessage({ success: false, message: response.data.message });
          }
        }

      } catch (error) {
        console.error("reCAPTCHA failed to execute:", error);
      }
    } catch (error) {
      setCaptchaError("An error occurred while submitting. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (toastMessage) {
      if (toastMessage.success) {
        toast.success(toastMessage.message);
      } else {
        toast.error(toastMessage.message);
      }
    }
  }, [toastMessage]);

  if (!ticket.is_rateable) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white shadow-lg h-auto rounded-lg w-2/6 p-10">
          <div className="flex justify-center mb-16">
            <img src="/images/logo_mark.png" alt="logo" className="w-48" />
          </div>

          <p className="flex justify-center text-xl text-gray-500 mb-6">
            This ticket is not suitable for rating at the moment.
          </p>
        </div>
      </div>
    );
  }
  
  if (ticket.rating_message) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white shadow-lg h-auto rounded-lg w-2/6 p-10">
          <div className="flex justify-center mb-16">
            <img src="/images/logo_mark.png" alt="logo" className="w-48" />
          </div>

          <p className="flex justify-center text-xl text-gray-500 mb-6">
            This ticket is already rated. Thank you for your feedback.
          </p>
        </div>
      </div>
    );
  }
  

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="bg-white shadow-lg h-auto rounded-lg w-2/6 p-10">
        <div className="flex justify-center mb-16">
          <img src="/images/logo_mark.png" alt="logo" className="w-48" />
        </div>

        <h2 className="flex justify-center text-3xl font-bold text-gray-800 mb-2">
          Rating for #{ticket.display_id}
        </h2>
        <p className="flex justify-center text-xl text-gray-500 mb-6">
          {ticket.subject}
        </p>

        <div className="flex justify-center mb-6">
          {[...Array(5)].map((_, index) => (
            <Star
              key={index}
              className={`h-8 w-8 ${
                index < star ? "text-yellow-500" : "text-gray-300"
              }`}
              fill={index < star ? "currentColor" : "none"}
            />
          ))}
        </div>

        <Form {...rateForm}>
          <form
            onSubmit={rateForm.handleSubmit(handleSubmit)}
            className="space-y-8 w-full"
          >
            <FormField
              control={rateForm.control}
              name="rating_message"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Comment</FormLabel>
                  <FormControl>
                    <TextArea
                      {...field}
                      value={field.value ?? ""}
                      placeholder="Write your comment here..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {captchaError && (
              <p className="text-red-500 text-sm mt-2 text-center">
                {captchaError}
              </p>
            )}
            <Button type="submit" className="flex w-full p-4 rounded-xl" disabled={isSubmitting} >
              {isSubmitting ? "Submitting..." : "SUBMIT"}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};

const RateTicket = ({ ticket, star }: Props) => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey="6Ldxv8UqAAAAAGgMnA3-0UnFt2rj6Dvn1cicVCdz">
      <RateTicketContent ticket={ticket} star={star} />
    </GoogleReCaptchaProvider>
  );
};

export default RateTicket;